import React, { useState, useEffect, useMemo } from 'react';
import { updateDoc, serverTimestamp } from 'firebase/firestore';
import { TASK_STATUS } from '../firebase.config.js';
import { useAgency } from '../AgencyContext';
import { useTaskTracker } from '../TaskTrack/useTaskTracker';
import './QAComponent.css';

const QAComponent = ({ task, taskId, user, userRole, onTaskUpdate }) => {
  const { trackEvent, updateEvent } = useTaskTracker();
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(-1);
  const [comment, setComment] = useState('');
  const [imageLoadStates, setImageLoadStates] = useState({});
  const [fixedImageOrder, setFixedImageOrder] = useState(null); // Fixed order set at landing
  const [modifiedInCurrentSession, setModifiedInCurrentSession] = useState(new Set()); // Track images modified in current session
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [magnifierPos, setMagnifierPos] = useState({ x: 0, y: 0 });
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [magnifierOffset, setMagnifierOffset] = useState({ x: 0, y: 0 });
  const [isZoomedIn, setIsZoomedIn] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [imageToReject, setImageToReject] = useState(null);
  const { getAgencyDoc } = useAgency();
  
  // Check if user is assigned to this task OR is a Manager
  const isAssignedToUser = task.assignedTo === user.email || userRole === 'Manager';
  
  const isL1Phase = task.status === TASK_STATUS.PENDING_QA_L1;
  const isL2Phase = task.status === TASK_STATUS.PENDING_QA_L2;
  
  // Get images to review based on phase - keep original order except for initial landing
  const allRelevantImages = useMemo(() => {
    const filteredImages = isL2Phase 
      ? task.images?.filter(img => img.isL2Image) || []
      : task.images?.filter(img => !img.isL2Image) || [];
    
    return filteredImages;
  }, [task.images, isL2Phase]);

  // Set fixed order only once at landing - NEVER changes after
  useEffect(() => {
    if (allRelevantImages.length > 0 && !fixedImageOrder) {
      // Determine if this is EVENT 1 (REVIEW) or EVENT 2 (REVIEW_BIS)
      const hasQualified = allRelevantImages.some(img => img.status === 'approved' || img.status === 'rejected');
      const hasUnqualified = allRelevantImages.some(img => img.status === 'pending' || !img.status);
      const isReviewBis = hasQualified && hasUnqualified;
      
      let initialOrder;
      if (!isReviewBis) {
        // EVENT 1 (REVIEW): Keep original order
        initialOrder = [...allRelevantImages];
      } else {
        // EVENT 2 (REVIEW_BIS): NOT_QUALIFIED on top, QUALIFIED below
        const notQualifiedImages = allRelevantImages.filter(img => 
          img.status === 'pending' || !img.status
        );
        const qualifiedImages = allRelevantImages.filter(img => 
          img.status === 'approved' || img.status === 'rejected'
        );
        initialOrder = [...notQualifiedImages, ...qualifiedImages];
      }
      
      setFixedImageOrder(initialOrder);
    }
  }, [allRelevantImages, fixedImageOrder]);

  // Use fixed order - never changes after initial set
  const imageList = fixedImageOrder || [];
  
  // Only pending images need review
  const pendingImages = allRelevantImages.filter(img => img.status === 'pending' || !img.status);
  
  const stats = {
    total: allRelevantImages.length,
    approved: allRelevantImages.filter(img => img.status === 'approved').length,
    rejected: allRelevantImages.filter(img => img.status === 'rejected').length,
    pending: allRelevantImages.filter(img => img.status === 'pending' || !img.status).length,
    toReview: pendingImages.length
  };

  // Progress should be based on reviewed images vs total task requirement
  const reviewedCount = stats.approved + stats.rejected;
  const progress = stats.total ? (reviewedCount / stats.total) * 100 : 0;
  
  // Updated logic for button states
  const canValidate = stats.pending === 0 && stats.rejected === 0 && stats.total > 0;
  const canSendForRework = stats.pending === 0 && stats.rejected > 0 && stats.total > 0;
  const allReviewed = stats.pending === 0 && stats.total > 0;

  // Show assignment warning if task is not assigned to current user
  const showAssignmentWarning = !isAssignedToUser;

  // Preload images for better performance
  useEffect(() => {
    allRelevantImages.forEach(image => {
      const img = new Image();
      img.onload = () => {
        setImageLoadStates(prev => ({ ...prev, [image.id]: 'loaded' }));
      };
      img.onerror = () => {
        setImageLoadStates(prev => ({ ...prev, [image.id]: 'error' }));
      };
      // Load smaller version if available, otherwise full image
      img.src = image.thumbnailUrl || image.url;
    });
  }, [allRelevantImages]);

  // Auto-select first image when fixed order is set
  useEffect(() => {
    if (fixedImageOrder && fixedImageOrder.length > 0 && !selectedImage && isAssignedToUser) {
      setSelectedImage(fixedImageOrder[0]);
      setSelectedImageIndex(0);
    }
  }, [fixedImageOrder, selectedImage, isAssignedToUser]);

  // Keyboard navigation using fixed order
  useEffect(() => {
    if (!isAssignedToUser || !fixedImageOrder) return;
    
    const handleKeyPress = (e) => {
      if (!fixedImageOrder || fixedImageOrder.length === 0) return;
      
      let newIndex = selectedImageIndex;
      
      if (e.key === 'ArrowLeft') {
        newIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : fixedImageOrder.length - 1;
      } else if (e.key === 'ArrowRight') {
        newIndex = selectedImageIndex < fixedImageOrder.length - 1 ? selectedImageIndex + 1 : 0;
      }
      
      if (newIndex !== selectedImageIndex && fixedImageOrder[newIndex]) {
        setSelectedImageIndex(newIndex);
        setSelectedImage(fixedImageOrder[newIndex]);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImageIndex, fixedImageOrder, isAssignedToUser]);

  const handleImageSelect = (image, index) => {
    if (!isAssignedToUser) {
      alert('You must assign this task to yourself before you can interact with it.');
      return;
    }
    setSelectedImage(image);
    setSelectedImageIndex(index);
    setVideoLoaded(false); // Reset video loading state when changing selection
  };

  const handleRejectClick = (image) => {
    setImageToReject(image);
    setRejectComment('');
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectComment.trim()) {
      alert('Please provide a reason for rejection.');
      return;
    }
    
    await handleImageReview(imageToReject, 'rejected', rejectComment.trim());
    setShowRejectModal(false);
    setRejectComment('');
    setImageToReject(null);
  };

  const handleRejectCancel = () => {
    setShowRejectModal(false);
    setRejectComment('');
    setImageToReject(null);
  };

  const handleImageReview = async (image, status, qaComment = '') => {
    if (!isAssignedToUser) {
      alert('You must assign this task to yourself before reviewing images.');
      return;
    }

    // QA can always modify their decisions at any time

    try {
      const updatedImages = task.images.map(img => {
        if (img.id === image.id) {
          const updatedImage = { 
            ...img, 
            status, 
            reviewedBy: user.email, 
            reviewedAt: new Date(),
            qaComment: qaComment || ''
          };
          
          // IMPORTANT: Track rejection history
          if (status === 'rejected') {
            updatedImage.hasBeenRejected = true;
            updatedImage.firstRejectedAt = img.firstRejectedAt || new Date();
            updatedImage.firstRejectedBy = img.firstRejectedBy || user.email;
            updatedImage.rejectionCount = (img.rejectionCount || 0) + 1;
          }
          
          return updatedImage;
        }
        return img;
      });

      // Track cumulative rejections at task level
      const taskRejectionStats = task.rejectionStats || {};
      if (status === 'rejected') {
        // Initialize user's rejection count if not exists
        if (!taskRejectionStats[image.uploadedBy]) {
          taskRejectionStats[image.uploadedBy] = {
            total: 0,
            isL2: image.isL2Image || false
          };
        }
        // Increment rejection count
        taskRejectionStats[image.uploadedBy].total += 1;
      }

      const taskDocRef = getAgencyDoc('taskbacklog', taskId);
      if (!taskDocRef) {
        console.error("Could not get agency document reference for QA review");
        return;
      }

      await updateDoc(taskDocRef, {
        images: updatedImages,
        rejectionStats: taskRejectionStats,
        lastModified: serverTimestamp(),
        lastModifiedBy: user.email
      });

      // Track QA events - always try to update existing event first
      const wasAlreadyReviewed = image.status && image.status !== 'pending';
      const previousStatus = image.status;
      
      if (wasAlreadyReviewed) {
        // Image was already reviewed - update existing event
        let actionName;
        if (previousStatus !== status) {
          // Status changed
          actionName = status === 'approved' ? 'Changed decision to Accepted' : 'Changed decision to Rejected';
        } else {
          // Same status clicked again - keep original action
          actionName = status === 'approved' ? 'Accepted media' : 'Rejected media';
        }
        
        const metadata = {
          imageId: image.id,
          imageUrl: image.url,
          imageName: image.name,
          previousDecision: previousStatus
        };
        
        if (status === 'rejected' && qaComment) {
          metadata.comment = qaComment;
        }
        
        const wasUpdated = await updateEvent(taskId, taskId, image.id, actionName, 'QA', task.quantity, metadata);
        
        if (!wasUpdated) {
          // Fallback to creating new event if update failed
          const fallbackAction = status === 'approved' ? 'Accepted media' : 'Rejected media';
          await trackEvent(taskId, taskId, fallbackAction, 'QA', task.quantity, metadata, task.isVideo);
        }
      } else {
        // This is a first review - create new event
        if (status === 'approved') {
          await trackEvent(taskId, taskId, 'Accepted media', 'QA', task.quantity, {
            imageId: image.id,
            imageUrl: image.url,
            imageName: image.name
          }, task.isVideo);
        } else if (status === 'rejected') {
          await trackEvent(taskId, taskId, 'Rejected media', 'QA', task.quantity, {
            imageId: image.id,
            comment: qaComment,
            imageUrl: image.url,
            imageName: image.name
          }, task.isVideo);
        }
      }

      // Track that this image was modified in current session
      setModifiedInCurrentSession(prev => new Set([...prev, image.id]));

      await onTaskUpdate();

      // Auto-navigate to next image after qualification using fixed order
      const nextIndex = selectedImageIndex + 1;
      if (nextIndex < fixedImageOrder.length) {
        setSelectedImage(fixedImageOrder[nextIndex]);
        setSelectedImageIndex(nextIndex);
      }
    } catch (error) {
      console.error('Review error:', error);
      alert('Error reviewing image');
    }
  };

  const handleTaskDecision = async (isValid) => {
    if (!isAssignedToUser) {
      alert('You must assign this task to yourself before making decisions.');
      return;
    }

    // Additional validation
    if (isValid && stats.rejected > 0) {
      alert('Cannot validate task with rejected images. Please review and approve all images or send for rework.');
      return;
    }

    if (!isValid && stats.rejected === 0) {
      alert('Cannot send for rework when all images are approved. Please reject at least one image first.');
      return;
    }

    try {
      let newStatus;
      
      if (isValid) {
        if (isL1Phase) {
          newStatus = task.hasL2Process ? TASK_STATUS.NOT_STARTED_L2 : TASK_STATUS.VALIDATED_L1;
        } else {
          newStatus = TASK_STATUS.VALIDATED_L2;
        }
      } else {
        newStatus = isL1Phase ? TASK_STATUS.TO_BE_REWORKED_L1 : TASK_STATUS.TO_BE_REWORKED_L2;
      }

      const updateData = {
        status: newStatus,
        statusChangedAt: serverTimestamp(),
        lastModified: serverTimestamp(),
        lastModifiedBy: user.email,
        assignedTo: null,
        assignedAt: null
      };

      // Update leaderboard stats for both validation and rejection scenarios
      const leaderboardStats = task.leaderboardStats || {};
      
      // Count ONLY the images that were modified in this current session
      // IMPORTANT: On ne compte que les nouvelles décisions, pas toutes les images !
      const currentSessionStats = {};
      
      // Si on valide (isValid = true), on compte les images "approved" qui étaient "pending"
      // Si on rejette (isValid = false), on compte les images "rejected" qui étaient "pending" 
      allRelevantImages.forEach(image => {
        if (image.uploadedBy && modifiedInCurrentSession.has(image.id)) {
          const uploaderEmail = image.uploadedBy;
          if (!currentSessionStats[uploaderEmail]) {
            currentSessionStats[uploaderEmail] = {
              approved: 0,
              rejected: 0,
              role: image.isL2Image ? 'L2' : 'L1'
            };
          }
          
          // Compter seulement les nouvelles décisions de cette session
          if (image.status === 'approved') {
            currentSessionStats[uploaderEmail].approved++;
          } else if (image.status === 'rejected') {
            currentSessionStats[uploaderEmail].rejected++;
          }
        }
      });


      // IMPORTANT: Always update leaderboard stats, even if only rejections
      if (Object.keys(currentSessionStats).length > 0) {
        // Update leaderboard stats for each uploader (L1/L2 users)
        Object.entries(currentSessionStats).forEach(([uploaderEmail, sessionStats]) => {
          if (!leaderboardStats[uploaderEmail]) {
            leaderboardStats[uploaderEmail] = {
              role: sessionStats.role,
              tasksCompleted: 0,
              imagesSubmitted: 0,
              imagesAccepted: 0,
              imagesRejected: 0,
              submissions: [],
              manager: task.createdBy || task.taskOwner || 'unknown'
            };
          }

          // Add ONLY this session's results to cumulative stats
          leaderboardStats[uploaderEmail].imagesAccepted += sessionStats.approved;
          leaderboardStats[uploaderEmail].imagesRejected += sessionStats.rejected;
          
          // Track this QA session
          leaderboardStats[uploaderEmail].submissions.push({
            timestamp: new Date(),
            phase: isValid ? (isL1Phase ? 'QA_L1_VALIDATED' : 'QA_L2_VALIDATED') : (isL1Phase ? 'QA_L1_REJECTED' : 'QA_L2_REJECTED'),
            imagesAccepted: sessionStats.approved,
            imagesRejected: sessionStats.rejected,
            qaUser: user.email,
            taskId: taskId
          });

        });
      }

      // ALWAYS update QA leaderboard stats (even if no images were processed)
      if (!leaderboardStats[user.email]) {
        leaderboardStats[user.email] = {
          role: 'QA',
          tasksCompleted: 0,
          imagesSubmitted: 0,
          imagesAccepted: 0,
          imagesRejected: 0,
          submissions: [],
          manager: task.createdBy || task.taskOwner || 'unknown'
        };
      }

      const totalApproved = Object.values(currentSessionStats).reduce((sum, stats) => sum + stats.approved, 0);
      const totalRejected = Object.values(currentSessionStats).reduce((sum, stats) => sum + stats.rejected, 0);

      leaderboardStats[user.email].tasksCompleted += 1;
      leaderboardStats[user.email].imagesAccepted += totalApproved;
      leaderboardStats[user.email].imagesRejected += totalRejected;
      leaderboardStats[user.email].submissions.push({
        timestamp: new Date(),
        phase: isValid ? (isL1Phase ? 'QA_L1_VALIDATE' : 'QA_L2_VALIDATE') : (isL1Phase ? 'QA_L1_REWORK' : 'QA_L2_REWORK'),
        imagesAccepted: totalApproved,
        imagesRejected: totalRejected,
        taskId: taskId
      });

      updateData.leaderboardStats = leaderboardStats;

      // Add comment if provided when sending for rework
      if (!isValid && comment.trim()) {
        const newComment = {
          text: comment.trim(),
          author: user.email,
          timestamp: new Date(),
          status: newStatus
        };
        
        const existingComments = task.comments || [];
        updateData.comments = [...existingComments, newComment];
      }

      const taskDocRef = getAgencyDoc('taskbacklog', taskId);
      if (!taskDocRef) {
        console.error("Could not get agency document reference for QA decision");
        return;
      }

      await updateDoc(taskDocRef, updateData);

      // Track QA task decision events
      if (isValid) {
        const validateAction = isL1Phase ? 'Validated task L1' : 'Validated task L2';
        await trackEvent(taskId, taskId, validateAction, 'QA', task.quantity);
      } else {
        const reworkAction = isL1Phase ? 'Sent for rework L1' : 'Sent for rework L2';
        const metadata = {
          rejectedImages: stats.rejected,
          approvedImages: stats.approved
        };
        if (comment.trim()) {
          metadata.comment = comment.trim();
        }
        await trackEvent(taskId, taskId, reworkAction, 'QA', task.quantity, metadata);
      }
      
      setComment('');
      
      // Redirect to backlog after a short delay
      setTimeout(() => {
        window.location.href = '/backlog';
      }, 500);
      
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Error processing task decision');
    }
  };

  const getImageStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'qa__image-badge--approved';
      case 'rejected': return 'qa__image-badge--rejected';
      default: return 'qa__image-badge--pending';
    }
  };

  return (
    <div className="qa">
      <div className="qa__content">
        {/* Left Panel */}
        <div className="qa__left-panel">
          {/* Task Info Card */}
          <div className="qa__task-info-card" title={`${task.taskName}${task.description ? '\n\n' + task.description : ''}`}>
            <div className="qa__task-title">
              {task.taskName && task.taskName.length > 30 
                ? `${task.taskName.substring(0, 30)}...` 
                : task.taskName
              }
            </div>
            {task.description && (
              <div className="qa__task-description">
                {task.description.length > 60 
                  ? `${task.description.substring(0, 60)}...` 
                  : task.description
                }
              </div>
            )}
          </div>
          <div className="qa__header">
            <h2 className="qa__title">Quality analysis · {stats.toReview}/{stats.total}</h2>
          </div>

          {/* Image List - Simple order, no sorting */}
          {imageList.length > 0 && (
            <div className="qa__image-list">
              {imageList.map((image, index) => {
                // Get current status from allRelevantImages to reflect real-time updates
                const currentImage = allRelevantImages.find(img => img.id === image.id) || image;
                
                return (
                <div key={image.id} className="qa__image-item">
                  <div
                    className={`qa__image-badge ${getImageStatusClass(currentImage.status)} ${
                      selectedImage?.id === image.id ? 'qa__image-badge--selected' : ''
                    }`}
                    onClick={() => handleImageSelect(currentImage, index)}
                  >
                    <span className="qa__image-badge-name">{currentImage.name}</span>
                    {currentImage.hasBeenRejected && (
                      <span className="qa__image-badge-rejected-marker" title="Has been rejected before">🔴</span>
                    )}
                  </div>
                  
                  {/* Show action buttons for selected image */}
                  {selectedImage?.id === image.id && (
                    <div className="qa__image-actions">
                      <button
                        className={`qa__reject-btn ${currentImage.status === 'rejected' ? 'qa__reject-btn--active' : ''}`}
                        onClick={() => handleRejectClick(currentImage)}
                        disabled={!isAssignedToUser}
                      >
                        ✕ Reject
                      </button>
                      <button
                        className={`qa__approve-btn ${currentImage.status === 'approved' ? 'qa__approve-btn--active' : ''}`}
                        onClick={() => handleImageReview(currentImage, 'approved')}
                        disabled={!isAssignedToUser}
                      >
                        ✓ Approve
                      </button>
                    </div>
                  )}
                  
                  {/* Reject Comment Modal */}
                  {showRejectModal && selectedImage?.id === image.id && (
                    <div className="qa__reject-modal">
                      <div className="qa__reject-modal-content">
            
                        <textarea
                          value={rejectComment}
                          onChange={(e) => setRejectComment(e.target.value)}
                          placeholder="Explain why it was rejected..."
                          className="qa__reject-textarea"
                          rows={3}
                          autoFocus
                        />
                        <div className="qa__reject-modal-actions">
                          <button 
                            className="qa__reject-cancel-btn"
                            onClick={handleRejectCancel}
                          >
                            Cancel
                          </button>
                          <button 
                            className="qa__reject-confirm-btn"
                            onClick={handleRejectConfirm}
                            disabled={!rejectComment.trim()}
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Center Panel */}
        <div className="qa__center-panel">
          {selectedImage ? (
            selectedImage.mediaType === 'video' ? (
              <div className="qa__video-container">
                {!videoLoaded && (
                  <div className="qa__video-loading">
                    <div className="qa__loading-text">Loading video...</div>
                  </div>
                )}
                <video 
                  src={selectedImage.url} 
                  className="qa__preview-video"
                  controls
                  preload="metadata"
                  style={{ opacity: videoLoaded ? 1 : 0 }}
                  onLoadedData={() => setVideoLoaded(true)}
                  onError={() => setVideoLoaded(true)}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : (
              <div className="qa__image-container">
                <img 
                  src={selectedImage.url} 
                  alt={selectedImage.name}
                  className="qa__preview-image"
                  loading="lazy"
                  onMouseEnter={() => setShowMagnifier(true)}
                  onMouseLeave={() => {
                    setShowMagnifier(false);
                    setIsZoomedIn(false);
                  }}
                  onClick={() => setIsZoomedIn(!isZoomedIn)}
                  onMouseMove={(e) => {
                    const img = e.currentTarget;
                    const rect = img.getBoundingClientRect();
                    
                    // Position de la souris relative à l'image
                    const x = e.clientX - rect.left;
                    const y = e.clientY - rect.top;
                    
                    // Stocker la vraie position du curseur
                    setCursorPos({ x: e.clientX, y: e.clientY });
                    
                    // Position de la loupe avec magnétisme pour les bords
                    const magnifierSize = isZoomedIn ? 320 : 220; // Taille de la loupe
                    const margin = 20; // Marge de sécurité
                    
                    let loupeX = e.clientX;
                    let loupeY = e.clientY;
                    
                    // Magnétisme vertical (haut/bas de l'écran)
                    if (e.clientY < magnifierSize / 2 + margin) {
                      // Proche du haut : curseur en haut de la loupe
                      loupeY = magnifierSize / 2 + margin;
                    } else if (e.clientY > window.innerHeight - magnifierSize / 2 - margin) {
                      // Proche du bas : curseur en bas de la loupe  
                      loupeY = window.innerHeight - magnifierSize / 2 - margin;
                    }
                    
                    // Magnétisme horizontal (gauche/droite de l'écran)
                    if (e.clientX < magnifierSize / 2 + margin) {
                      // Proche de la gauche : curseur à gauche de la loupe
                      loupeX = magnifierSize / 2 + margin;
                    } else if (e.clientX > window.innerWidth - magnifierSize / 2 - margin) {
                      // Proche de la droite : curseur à droite de la loupe
                      loupeX = window.innerWidth - magnifierSize / 2 - margin;
                    }
                    
                    setMagnifierPos({ 
                      x: loupeX, 
                      y: loupeY 
                    });
                    
                    // Calcul correct du background position
                    const xPercent = (x / rect.width) * 100;
                    const yPercent = (y / rect.height) * 100;
                    
                    setMagnifierOffset({
                      x: xPercent,
                      y: yPercent
                    });
                  }}
                />
                
                {showMagnifier && (
                  <>
                    <div 
                      className={`qa__magnifier ${isZoomedIn ? 'qa__magnifier--zoomed' : ''}`}
                      style={{
                        left: magnifierPos.x,
                        top: magnifierPos.y,
                        backgroundImage: `url(${selectedImage.url})`,
                        backgroundPosition: `${magnifierOffset.x}% ${magnifierOffset.y}%`,
                        backgroundSize: '500%',
                        backgroundRepeat: 'no-repeat'
                      }}
                    />
                    <div 
                      className="qa__cursor-dot"
                      style={{
                        left: cursorPos.x,
                        top: cursorPos.y
                      }}
                    />
                  </>
                )}
              </div>
            )
          ) : (
            <div className="qa__center-placeholder">
              Select a media file to review
            </div>
          )}

          {/* Task Actions - Bottom Right like L1 */}
          <div className="qa__task-actions">
            {/* Status Summary */}
            <div className="qa__stats-summary">
              <span className="qa__stat-item qa__stat-item--rejected">{stats.rejected} Rejected</span>
              <span className="qa__stat-item qa__stat-item--approved">{stats.approved} Approved</span>
              <span className="qa__stat-item qa__stat-item--remaining">{stats.toReview} Remaining</span>
            </div>


            {/* Final Action Buttons - Conditional visibility */}
            <div className="qa__final-actions">
              {stats.rejected > 0 && (
                <button
                  className={`qa__rework-btn ${canSendForRework ? '' : 'qa__rework-btn--disabled'}`}
                  onClick={() => handleTaskDecision(false)}
                  disabled={!canSendForRework || !isAssignedToUser}
                >
                  Send for rework
                </button>
              )}
              {stats.rejected === 0 && (
                <button
                  className={`qa__validate-btn ${canValidate ? '' : 'qa__validate-btn--disabled'}`}
                  onClick={() => handleTaskDecision(true)}
                  disabled={!canValidate || !isAssignedToUser}
                >
                  Validate
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assignment Warning Overlay */}
      {showAssignmentWarning && (
        <div className="qa__assignment-overlay">
          <div className="qa__assignment-warning">
            <div className="qa__warning-icon">🔒</div>
            <h3>Task Not Assigned</h3>
            <p>You must assign this task to yourself using the "Take Task" button in the header before you can work on it.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default QAComponent;